<template>
    <b-table
        :loading="isLoading"
        :data="keywords.data"
        :mobile-cards="true"
        :checked-rows.sync="checkedRows"
        :checkable="!readonly"
        :custom-is-checked="(props, row) => props.id === row.id"
        paginated
        pagination-rounded
        backend-pagination
        :total="total"
        :per-page="per_page"
        :row-class="(row) => getRowClass(row)"
        default-sort="ID"
        default-sort-direction="DESC"
        striped
        @page-change="onPageChange"
    >
        <b-table-column field="ID" sortable label="ID">
            <div slot="default" slot-scope="props">
                {{ props.row.id }}
            </div>
        </b-table-column>
        <b-table-column field="to_work_on" :visible="!readonly">
            <div slot="default" slot-scope="props" class="is-flex is-justify-content-center is-align-items-center">
                <div v-if="props.row.monitorank_status === 'pending'" style="height: 100%;">
                    <b-tooltip
                        label="Données en attente de traitement"
                        position="is-top"
                    >
                        <i class="fas fa-info-circle has-text-info" style="font-size: 1.5rem;" />
                    </b-tooltip>
                </div>

                <div v-if="props.row.monitorank_status === 'failed'" style="height: 100%;">
                    <b-tooltip
                        label="Echec du traitement, cliquez pour relancer"
                        position="is-top"
                    >
                        <i
                            @click="reloadMonitorankData(props.row)"
                            class="fas fa-sync clickable-icon"
                           style="font-size: 1.5rem;" />
                    </b-tooltip>
                </div>

                <b-switch
                    v-else-if="props.row.monitorank_status === 'success'"
                    v-model="props.row.to_work_on"
                    type="is-success"
                    :disabled="readonly"
                    :true-value="props.row.to_work_on != null ? props.row.to_work_on : true"
                    :false-value="null"
                    @input="changeStatus([props.row.id], props.row.to_work_on)"
                />
            </div>

            <div v-if="!readonly" slot="header">
                <b-switch
                    v-show="!readonly && checkedRows.length > 0"
                    v-model="mainSwitch"
                    type="is-success"
                    @input="
                changeStatus(
                    checkedRows.map(e => e.id),
                    mainSwitch
                )
            "
                />
            </div>
        </b-table-column>

        <b-table-column field="query_id" :label="$t('mes-accompagnements.request')">
            <div slot="default" slot-scope="props">
                <span v-if="props.row.query_id">
                    <i class="fa fa-chart-line text-success" />
                </span>
                <span v-else>
                    <i class="fa fa-chart-line text-danger" />
                </span>
                <a role="button" @click="goToSearch(props.row.mot_cle)">
                    {{ props.row.mot_cle }}
                </a>
                <nl-clipboard-button :copy="props.row.mot_cle" />
                <b-tooltip v-if="props.row.note_divers" position="is-top">
                    <template v-slot:content>
                        {{ props.row.note_divers }}
                    </template>
                    <i class="fas fa-comment-dots text-danger" />
                </b-tooltip>
            </div>
        </b-table-column>
        <b-table-column field="url" :label="$t('mes-accompagnements.urltopush')">
            <div slot="default" slot-scope="props" v-if="props.row.monitorank_status === 'success'">
                <div class="d-flex align-items-center">
                    <span>{{ getPathName(props.row.url) }}</span>
                    <nl-clipboard-button :copy="props.row.url" />
                </div>
            </div>
        </b-table-column>
        <b-table-column field="opti_kw" label="RD" centered>
            <div slot="default" slot-scope="props" v-if="props.row.monitorank_status === 'success'">
                <campaign-ref-domains :keyword="props.row" :readonly="readonly" />
            </div>
        </b-table-column>
        <b-table-column field="id" :label="$t('mes-accompagnements.opti')" centered>
            <div slot="default" slot-scope="props" v-if="props.row.monitorank_status === 'success'">
                {{ props.row.opti_kw }}%
            </div>
        </b-table-column>
        <b-table-column field="position_initiale" :label="$t('mes-accompagnements.manualposition-alt')" centered>
            <div slot="default" slot-scope="props" v-if="props.row.monitorank_status === 'success'">
                {{ props.row.position_initiale }}
            </div>
        </b-table-column>
        <b-table-column field="position" :label="$t('mes-accompagnements.lastposition-alt')" centered>
            <div slot="default" slot-scope="props" v-if="props.row.monitorank_status === 'success'">
                <div class="d-flex flex-column">
                    <span v-if="props.row.position === -1"> OUT </span>
                    <span v-else-if="props.row.position > 0">
                        {{ props.row.position }}
                    </span>
                    <span v-else> - </span>
                    <span
                        v-if="props.row.check_date"
                        class="badge"
                        :class="`${isToday(new Date(props.row.check_date)) ? 'badge-success' : 'badge-danger'}`"
                    >
                        {{ $d(Date.parse(props.row.check_date), 'short') }}
                    </span>
                </div>
            </div>
        </b-table-column>
        <b-table-column field="position_variation" :label="$t('mes-accompagnements.variation-alt')" centered>
            <div slot="default" slot-scope="props" v-if="props.row.monitorank_status === 'success'">
                <a href="#" @click="$emit('openPositionModal', props.row)">
                    <strong v-if="props.row.position === -1 || !props.row.position" class="text-danger"> OUT </strong>
                    <strong v-else-if="props.row.position_variation === 0">=</strong>
                    <strong v-else-if="props.row.position_variation > 0" class="text-success">
                        + {{ props.row.position_variation }}
                    </strong>
                    <strong v-else-if="props.row.position_variation < 0" class="text-danger">
                        - {{ Math.abs(props.row.position_variation) }}
                    </strong>
                </a>
            </div>
        </b-table-column>
        <b-table-column field="url_extension" :label="$t('language.title')" centered>
            <div slot="default" slot-scope="props" class="d-flex" v-if="props.row.monitorank_status === 'success'">
                <flag
                    :title="`google.${props.row.url_extension}`"
                    class="rounded shadow-sm d-block mr-2"
                    :iso="props.row.lang === 'en' ? 'gb' : props.row.lang"
                />
                <div :title="`google.${props.row.url_extension}`">.{{ props.row.url_extension }}</div>
            </div>
        </b-table-column>
        <b-table-column field="actions" :label="$t('global.actions')">
            <div slot="default" slot-scope="props" v-if="props.row.monitorank_status === 'success'">
                <url-form
                    v-if="!readonly"
                    :url="props.row"
                    @updateKeyword="updateKeyword"
                    @deleteKeyword="deleteKeyword"
                />
            </div>
            <div slot="header">
                <button
                    v-if="!readonly && checkedRows.length > 0"
                    class="rounded button is-light text-danger rounded-lg text-decoration-none"
                    @click.prevent="deleteKeyword(null, true)"
                >
                    <b-icon icon="trash" type="is-danger" class="m-0" />
                </button>
            </div>
        </b-table-column>
        <b-table-column field="partner_availability" sortable label="Dispo Partenaire">
            <div slot="default" slot-scope="props">
                <b-switch
                    v-if="props.row.monitorank_status === 'success'"
                    v-model="props.row.partner_availability"
                    type="is-success"
                    true-value="1"
                    false-value="0"
                    :disabled="readonly"
                    @input="changeAvailability(props.row.id, props.row.partner_availability)"
                >
                    {{ props.row.partner_availability ? 'Oui' : 'Non' }}
                </b-switch>
            </div>
        </b-table-column>
    </b-table>
</template>

<script>
    import { mapActions, mapMutations, mapState } from 'vuex'
    import UrlForm from './UrlForm'
    import NlClipboardButton from '@/components/UI/NlClipboardButton.vue'
    import CampaignRefDomains from '@/components/Campaigns/CampaignRefDomains.vue'

    export default {
        name: 'UrlTable',
        components: { CampaignRefDomains, NlClipboardButton, UrlForm },
        props: {
            keywords: {
                required: true
            },
            isLoading: {
                type: Boolean,
                default: false,
                required: true
            },
            readonly: {
                type: Boolean,
                default: false
            },
            total: {
                type: Number,
                default: 0,
                required: true
            },
            per_page: {
                type: Number,
                default: 0,
                required: true
            },
            page: {
                type: Number,
                default: 0,
                required: true
            }
        },
        data() {
            return {
                checkedRows: [],
                mainSwitch: true
            }
        },
        computed: {
            ...mapState('campaigns', {
                url: state => state.campaign.information.url
            })
        },
        methods: {
            ...mapActions('campaigns', ['fetchGoogleResults']),
            ...mapMutations('search', {
                setFilters: 'setFilters'
            }),
            getPathName(url) {
                return new URL(url).pathname
            },
            isToday: function (someDate) {
                const today = new Date()
                return (
                    someDate.getDate() === today.getDate() &&
                    someDate.getMonth() === today.getMonth() &&
                    someDate.getFullYear() === today.getFullYear()
                )
            },
            onPageChange(page) {
                this.$emit('pageChange', page)
            },
            changeStatus(ids, status) {
                this.$emit('changeStatus', ids, status)
            },
            changeAvailability(id, availability) {
                this.$emit('changeAvailability', id, availability)
            },
            updateKeyword() {
                this.$emit('updateKeyword')
            },
            deleteKeyword(props, bulk = false) {
                let url = null
                if (bulk === true) {
                    url = this.checkedRows
                } else {
                    url = [props]
                }
                this.$emit('deleteKeyword', url)
            },
            goToSearch(keyword) {
                const route = this.$router.resolve({
                    name: 'SearchByKeywords',
                    query: { keywordSearch: keyword }
                })
                window.open(route.href, '_blank')
            },
            getRowClass(row) {
                if (row.monitorank_status === 'pending') return 'row-pending'
                if (row.monitorank_status === 'failed') return 'row-failed'
                return ''
            },
            reloadMonitorankData(row) {

                const params = {
                    keywords: [row.mot_cle],
                    lang: row.lang,
                    version: row.url_extension,
                    url: row.url,
                    accompagnement_id: row.accompagnement_id,
                }

                this.fetchGoogleResults(params)
                    .then(results => {
                        this.$emit('refreshKeywords');
                        this.$buefy.toast.open({
                            message: 'Données rechargées avec succès',
                            type: 'is-success'
                        });
                    })
                    .catch(error => {
                        console.error("Erreur lors du rechargement:", error);

                        this.$buefy.toast.open({
                            message: 'Erreur lors du rechargement des données',
                            type: 'is-danger'
                        });
                    })
            }
        }
    }
</script>
<style>
    .row-pending {
        background-color: #f0f0f0 !important;
    }

    .row-failed {
        background-color: #ffe5e5 !important;
    }

    .clickable-icon {
        cursor: pointer;
        transition: all 0.3s ease;
    }

    .clickable-icon:hover {
        color: #ff1744;
        transform: scale(1.1) rotate(90deg);
    }

    .clickable-icon:active {
        transform: scale(0.95) rotate(180deg);
    }

    .clickable-icon.spinning {
        animation: spin 1s linear infinite;
    }

    @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
    }
</style>

